import { motion } from "motion/react";
import React, { useEffect } from "react";
import { IoDocumentTextOutline } from "react-icons/io5";
import { MdBuildCircle } from "react-icons/md";
import { GiAutoRepair } from "react-icons/gi";
import { IoMdTime } from "react-icons/io";
import { CgSandClock } from "react-icons/cg";
import { FaCheckCircle } from "react-icons/fa";
import { ClipLoader } from "react-spinners";
import { useDispatch, useSelector } from "react-redux";
import { fetchRepairsAsync } from "../../../../../store/Slice/repairSlice";
import type { RootState, AppDispatch } from "../../../../../store/store";

interface Props {
  setCardFilter: (
    filter:
      | "all"
      | "repair"
      | "replacement"
      | "pending"
      | "delayed"
      | "approved"
  ) => void;
  cardFilter:
  | "all"
  | "repair"
  | "replacement"
  | "pending"
  | "delayed"
  | "approved";
}

const RepairDashboardCards: React.FC<Props> = ({ setCardFilter, cardFilter }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { items, status } = useSelector((state: RootState) => state.repairs);

  const today = new Date();

  const totalOrders = items.length;
  const totalRepair = items.filter((i) => i.requestType === "repair").length;
  const totalReplacement = items.filter((i) => i.requestType === "replacement").length;

  const totalPending = items.filter((i) => {
    if (!i.expectedDeliveryDate) return true;
    return new Date(i.expectedDeliveryDate) >= today;
  }).length;

  const totalDelayed = items.filter((i) => {
    if (!i.expectedDeliveryDate) return false;
    return new Date(i.expectedDeliveryDate) < today;
  }).length;

  const totalApproved = items.filter((i) => i.isApproved === true).length;

  useEffect(() => {
    dispatch(fetchRepairsAsync({ page: 1, limit: 1000 }) as any);
  }, [dispatch]);

  const handleClick = (
    filter: "all" | "repair" | "replacement" | "pending" | "delayed" | "approved"
  ) => {
    setCardFilter(filter);
  };

  const CARD_COLORS: Record<
    string,
    { text: string; bg: string; iconBg: string }
  > = {
    all: {
      text: "text-blue-600",
      bg: "bg-blue-600",
      iconBg: "bg-blue-100 text-blue-600",
    },
    repair: {
      text: "text-indigo-600",
      bg: "bg-indigo-600",
      iconBg: "bg-indigo-100 text-indigo-600",
    },
    replacement: {
      text: "text-green-600",
      bg: "bg-green-600",
      iconBg: "bg-green-100 text-green-600",
    },
    pending: {
      text: "text-yellow-600",
      bg: "bg-yellow-500",
      iconBg: "bg-yellow-100 text-yellow-600",
    },
    delayed: {
      text: "text-orange-600",
      bg: "bg-orange-500",
      iconBg: "bg-orange-100 text-orange-600",
    },
    approved: {
      text: "text-emerald-600",
      bg: "bg-emerald-600",
      iconBg: "bg-emerald-100 text-emerald-600",
    },
  };


  const cardClass = (type: string) =>
    `${cardFilter === type
      ? `${CARD_COLORS[type].bg} text-white`
      : "bg-white dark:bg-zinc-950"
    } p-4 rounded-lg shadow 
   flex justify-between cursor-pointer font-semibold transition-all`;



  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 md:gap-6 mb-6 text-sm">

        {/* Total Orders */}
       <motion.div
  initial={{ y: 0 }}
  whileHover={{ y: -5 }}
  animate={{ y: cardFilter === "all" ? -5 : 0 }}
  className={cardClass("all")}
  onClick={() => handleClick("all")}
>
  <div className="flex flex-col items-start gap-3">
    <p
      className={
        cardFilter === "all"
          ? "text-white"
          : "text-[var(--theme-color)]"
      }
    >
      Total Orders
    </p>

    <p
      className={`text-3xl font-bold ${
        cardFilter === "all"
          ? "text-white"
          : "text-gray-900 dark:text-white"
      }`}
    >
      {status === "loading" ? (
        <ClipLoader size={20} color={cardFilter === "all" ? "#fff" : "#2563eb"} />
      ) : (
        totalOrders
      )}
    </p>
  </div>

  <IoDocumentTextOutline
    className={`text-5xl p-2 rounded-lg transition-all ${
      cardFilter === "all"
        ? "bg-white/20 text-white"
        : "bg-blue-100 text-[var(--theme-color)]"
    }`}
  />
</motion.div>


        {/* Repair */}
        <motion.div
          initial={{ y: 0 }}
          whileHover={{ y: -5 }}
          animate={{ y: cardFilter === "repair" ? -5 : 0 }}
          className={cardClass("repair")}
          onClick={() => handleClick("repair")}
        >
          <div className="flex flex-col items-start gap-3">
            <p className={`${cardFilter === "repair" ? "text-white" : "text-[var(--theme-color)]"}`}>Total Repair</p>
            <p className="text-3xl font-bold">{totalRepair}</p>
          </div>
          <MdBuildCircle className="text-blue-600 text-5xl bg-blue-100 p-2 rounded-lg" />
        </motion.div>

        {/* Replacement */}
        <motion.div
          initial={{ y: 0 }}
          whileHover={{ y: -5 }}
          animate={{ y: cardFilter === "replacement" ? -5 : 0 }}
          className={cardClass("replacement")}
          onClick={() => handleClick("replacement")}
        >
          <div className="flex flex-col items-start gap-3">
            <p className={`${cardFilter === "replacement" ? "text-white" : "text-[var(--theme-color)]"}`}>Total Replacement</p>
            <p className="text-3xl font-bold">{totalReplacement}</p>
          </div>
          <GiAutoRepair className="text-green-600 text-5xl bg-green-100 p-2 rounded-lg" />
        </motion.div>

        {/* Pending */}
        <motion.div
          initial={{ y: 0 }}
          whileHover={{ y: -5 }}
          animate={{ y: cardFilter === "pending" ? -5 : 0 }}
          className={cardClass("pending")}
          onClick={() => handleClick("pending")}
        >
          <div className="flex flex-col items-start gap-3">
            <p className={`${cardFilter === "pending" ? "text-white" : "text-[var(--theme-color)]"}`}>Pending</p>
            <p className="text-3xl font-bold">{totalPending}</p>
          </div>
          <IoMdTime className="text-yellow-600 text-5xl bg-yellow-100 p-2 rounded-lg" />
        </motion.div>

        {/* Delayed */}
        <motion.div
          initial={{ y: 0 }}
          whileHover={{ y: -5 }}
          animate={{ y: cardFilter === "delayed" ? -5 : 0 }}
          className={cardClass("delayed")}
          onClick={() => handleClick("delayed")}
        >
          <div className="flex flex-col items-start gap-3">
            <p className={`${cardFilter === "delayed" ? "text-white" : "text-[var(--theme-color)]"}`}>Delayed</p>
            <p className="text-3xl font-bold">{totalDelayed}</p>
          </div>
          <CgSandClock className="text-orange-600 text-5xl bg-orange-100 p-2 rounded-lg" />
        </motion.div>

        {/* Approved */}
        <motion.div
          initial={{ y: 0 }}
          whileHover={{ y: -5 }}
          animate={{ y: cardFilter === "approved" ? -5 : 0 }}
          className={cardClass("approved")}
          onClick={() => handleClick("approved")}
        >
          <div className="flex flex-col items-start gap-3">
            <p className={`${cardFilter === "approved" ? "text-white" : "text-[var(--theme-color)]"}`}>Completed</p>
            <p className="text-3xl font-bold">{totalApproved}</p>
          </div>
          <FaCheckCircle className="text-green-500 text-5xl bg-green-100 p-2 rounded-lg" />
        </motion.div>

      </div>
    </div>
  );
};

export default RepairDashboardCards;
